// this form is also used on the vehicle page in a modal

import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { Formik, Form } from 'formik';
import Button from '../../../audi-ui-components/Button';
import IconMyDealer from '../../../audi-ui-components/icons/MyDealer';
import IconSelect from '../../../audi-ui-components/icons/Select';
import IconCaution from '../../../audi-ui-components/icons/Caution';
import FormField from '../../../components/FormField';
import LoadingOverlay from '../../../components/LoadingOverlay';
import { request } from '../../../lib/apiRequestWrapper';
import { gtmPush } from '../../../lib/gtm';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import { FIELD_SELECT, FIELD_DEALER_SELECT, OPTS_STATES } from '../../../constants';

import { connect } from 'react-redux';
import { updateProfile, getPreferredServiceLocation } from '../../../redux/Profile';
const mapStateToProps = state => {
    return {
        isAuthed: state.profile.isAuthed,
        profileServiceLocation: state.profile.preferredServiceLocation,
        profileState: state.profile.data ? state.profile.data.homeState : null,
        isLoadingProfile: state.profile.isLoading
    }
}

const mapDispatchToProps = dispatch => {
    return {
        updateProfile: (formData, cb, errCb) => { dispatch(updateProfile(formData, cb, errCb)); },
        getPreferredServiceLocation: (id) => { dispatch(getPreferredServiceLocation(id)); }
    }
}

const PreferredServiceDealerForm = ({
  isAuthed, profileServiceLocation, profileState, isLoadingProfile, updateProfile, getPreferredServiceLocation,
  alwaysShowForm, gtmEventLabel, onCancel, className, style = {}, loadingBgColour
}) => {

    const [showForm, setShowForm] = useState(!profileServiceLocation || alwaysShowForm);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [error, setError] = useState(false);
    const [dealers, setDealers] = useState([]);
    const [isLoadingDealers, setIsLoadingDealers] = useState(false);
    const [dealersError, setDealersError] = useState(null);
    
    useEffect(()=>{
      if(showForm && (!dealers || dealers.length === 0)) {
        fetchDealers();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showForm]);
    
    const fetchDealers = () => {
      request(
        process.env.RAZZLE_GQLAPI,
        {
          method: "POST",
          body: JSON.stringify({
            query: /* GraphQL */`query ($skip: Int, $sort: LocationDtoSortInput!, $filter: LocationDtoFilterInput) {
              locationList(skip: $skip, take: 50, order: [$sort], where: $filter) {
                items {
                  id locationId name state webKey
                  dealer {
                    dealerCode dealerId name
                  }
                }
                totalCount
              }
            }`,
            variables: {
              skip: 0,
              sort: {name: "ASC"},
              filter: {type: {eq: "Service"}, status: {eq: true}}
            }
          })
        }
      ).then(response => {
        // console.log(response);
        let items = response.data?.locationList?.items;
        if (items?.length > 0) {
          setDealers(items);
        } else {
          let msg = "We couldn't find any dealers";
          setDealersError(msg);
        }
        setIsLoadingDealers(false);
      }).catch(err => {
        // console.log(err);
        let msg = "We couldn't find any dealers";
        setDealersError(msg);
        setIsLoadingDealers(false);
      })
    }

    const handleSubmit = (values, formikBag) => {
        if (values.preferredServiceLocation) {
            setIsUpdating(true);
            setIsSaved(false);
            setError(false);
            updateProfile(
                {preferredServiceLocation: values.preferredServiceLocation},
                () => {
                    getPreferredServiceLocation(values.preferredServiceLocation);
                    formikBag.resetForm({ values });
                    setIsUpdating(false);
                    setIsSaved(true);
                    gtmPush("myAudi", "preferredDealerUpdateSuccess", gtmEventLabel);
                    if (typeof onCancel === 'function') { onCancel(); }
                },
                (error) => {
                    setIsUpdating(false);
                    if (error.body && error.body.modelState) {
                        let errors = {};
                        for (let key in error.body.modelState) {
                            if (!Object.hasOwnProperty(key)) {
                                errors[key] = error.body.modelState[key][0];
                            }
                        }
                        formikBag.setStatus(errors);
                    }
                }
            );
        } else {
            formikBag.setErrors({preferredServiceLocation: "Please select a service centre"});
        }
    }

    const handleCancel = () => {
        setIsSaved(false);
        setError(false);
        if (!alwaysShowForm) {
            setShowForm(false);
        }
        if (typeof onCancel === 'function') { onCancel(); }
    }

    return (
        <div className="loading-wrapper">
            <div className={cn("py-7", className)} style={style}>

                {(isLoadingProfile || isLoadingDealers || isUpdating) &&
                    <LoadingOverlay type={"overlay"} bgColor={loadingBgColour} />
                }

                <div className="row">
                    <div className="col-12 col-medium-2 d-flex justify-content-center justify-content-medium-end icon-wrapper pr-4">
                        <IconMyDealer large />
                    </div>
                    <div className="col-12 col-medium-8 px-6 px-medium-0">
                        <h2 className="aui-headline-4 mb-5 headline-icon">
                            Preferred Service Centre
                        </h2>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 col-medium-8 offset-medium-2 px-6 px-medium-0">
                        {!showForm && <div>
                            <p className="">Your preferred service centre is <b>{profileServiceLocation ? profileServiceLocation.name : `...`}</b></p>
                            <p><Button buttonType="text" label="Change" onClick={()=>{ setShowForm(true); }} /></p>
                        </div>}

                        {showForm && <div>
                            <p className="mb-3">Please select your preferred Audi Service Centre.</p>
                            <Formik onSubmit={handleSubmit} initialValues={{ preferredServiceLocation: "", dealerState: profileState }}>
                                {(formikBag) => (
                                    <Form>
                                        <FormField
                                            name="dealerState"
                                            fieldType={FIELD_SELECT}
                                            label="State"
                                            options={OPTS_STATES}
                                            onChange={() => { formikBag.setFieldValue('preferredServiceLocation', ''); }}
                                            formikBag={formikBag}
                                        />
                                        <FormField
                                            name="preferredServiceLocation"
                                            fieldType={FIELD_DEALER_SELECT}
                                            label="Preferred Service Centre"
                                            dealers={formikBag.values.dealerState ? _filter(dealers, { state: formikBag.values.dealerState }) : dealers}
                                            valueProp="locationId"
                                            formikBag={formikBag}
                                            description={isLoadingDealers ? "Loading..." : ""}
                                        />

                                        <p style={{ textAlign: 'right' }}>
                                            {(isSaved && !formikBag.dirty) &&
                                                <span className="aui-color-text-green px-3"><IconSelect small /> Saved</span>
                                            }
                                            {error &&
                                                <span className="aui-color-text-red px-3"><IconCaution small /> Not saved</span>
                                            }
                                            {(!alwaysShowForm && profileServiceLocation) &&
                                                <Button buttonType="secondary" label="Cancel" onClick={handleCancel} />
                                            }
                                            &nbsp;
                                            <Button buttonType="primary" label="Submit" type="submit" />
                                        </p>
                                    </Form>
                                )}
                            </Formik>
                        </div>}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(PreferredServiceDealerForm);
